import React, { useState, useMemo, useEffect, useLayoutEffect } from "react";
import { ReparabilityImpactMatrixCard } from "../../../../RepairabilityMatrix";
import { DateTime } from "luxon";
import { translationLocale } from "../../../../../../i18n/locale";
import capitalize from "../../../../../../lib/capitalize";
import { useTranslations } from "../../../../../../api/useTranslations";
import type { DiagnosisReporting } from "../../../../../../api/useReporting";
import { styled } from "@linaria/react";
import { colors } from "@ui/Theme";
import { useReparabilityMatrixPeriod } from "../useReparabilityMatrixPeriod";
import cn from "classnames";
import Card from "@ui/Card";
import { exportReparabilityMatrix } from "../../../../RepairabilityMatrix/exportReparabilityMatrix";
import { useReparabilityDescriptions } from "../../../../RepairabilityMatrix/faultMappingUtils";
import ReportingChartSubtitle from "../../../ReportingChart/Subtitle";
import { MemoizedReportingMatrixTitle } from "../../../ReportingChart/Title";

const MatrixContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 500px;
  position: relative;
  border: 1px solid ${colors.hydrogen};
  border-radius: 4px;
  background: ${colors.hydrogen};
  overflow: hidden;
`;

interface ReparabilityImpactMatrixChartProps {
  reporting: DiagnosisReporting;
  matrixSelector?: React.ReactNode;
  dateSelector?: React.ReactNode;
  title: string;
  description?: {
    title: string;
    description: string;
    options?: { value: string; label: string; description: string }[];
  };
}

function ReparabilityImpactMatrixChart({
  reporting,
  matrixSelector,
  dateSelector,
  title,
  description,
}: ReparabilityImpactMatrixChartProps) {
  const translation = useTranslations();
  const reparabilityDescriptions = useReparabilityDescriptions();
  const [selectedReparabilityMatrixPeriod] = useReparabilityMatrixPeriod({});
  const [filters, setFilters] = useState({
    selectedYear: 0,
    selectedMonth: 0,
    selectedPlants: [] as string[],
    displayMode: "degradationType" as const,
    electricityPrice: 60,
    costMode: "hardcoded" as "hardcoded" | "calculated",
    sortByCost: false,
    useColorfulPlants: false,
  });

  // Extract year and month from selected period
  const { year, month } = useMemo(() => {
    if (!selectedReparabilityMatrixPeriod) {
      return { year: 0, month: 0 };
    }

    const date = DateTime.fromMillis(selectedReparabilityMatrixPeriod);
    return {
      year: date.year,
      month: date.month,
    };
  }, [selectedReparabilityMatrixPeriod]);

  // Update filters when period changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      selectedYear: year,
      selectedMonth: month,
    }));
  }, [year, month]);

  // Get display info for the subtitle
  const matrixInfo = useMemo(() => {
    if (!selectedReparabilityMatrixPeriod) return "All Data";

    try {
      return capitalize(
        DateTime.fromMillis(selectedReparabilityMatrixPeriod)
          .setLocale(translationLocale)
          .toFormat("MMMM yyyy"),
      );
    } catch {
      return "All Data";
    }
  }, [selectedReparabilityMatrixPeriod]);

  // Prepare available plant IDs for the matrix
  const availablePlantIds = useMemo(() => {
    if (!reporting || !reporting.periods || !reporting.projectById) return [];

    try {
      const projectIds = new Set<string>();

      for (const period of reporting.periods) {
        // If a period is selected, only include projects from that period
        if (year !== 0 && month !== 0) {
          const periodDate = new Date(period.period);
          if (periodDate.getFullYear() !== year || periodDate.getMonth() + 1 !== month) {
            continue;
          }
        }

        for (const value of period.values) {
          if (value.projectId) {
            projectIds.add(value.projectId);
          }
        }
      }

      return Array.from(projectIds);
    } catch {
      return [];
    }
  }, [reporting, year, month]);

  useLayoutEffect(() => {
    setFilters(prev => ({
      ...prev,
      selectedPlants: availablePlantIds,
    }));
  }, [availablePlantIds]);

  // Handle export button click
  const handleExport = () => {
    // Type assertion to handle the type mismatch between DiagnosisReporting and MatrixReporting
    exportReparabilityMatrix(
      reporting as unknown as Parameters<typeof exportReparabilityMatrix>[0],
      translation,
      filters,
      reparabilityDescriptions,
    );
  };

  return (
    <MatrixReportingCard
      reporting={reporting}
      handleExport={handleExport}
      title={title}
      matrixSelector={matrixSelector}
      dateSelector={dateSelector}
      extraSubtitleInfo={matrixInfo}
      description={description}
      customContent={
        <MatrixContainer>
          <ReparabilityImpactMatrixCard
            selectedYear={filters.selectedYear}
            selectedMonth={filters.selectedMonth}
            selectedPlants={filters.selectedPlants}
          />
        </MatrixContainer>
      }
    />
  );
}

interface MatrixReportingCardProps {
  reporting: DiagnosisReporting;
  handleExport: () => void;
  title: string;
  matrixSelector?: React.ReactNode;
  dateSelector?: React.ReactNode;
  description?: {
    title: string;
    description: string;
    options?: { value: string; label: string; description: string }[];
  };
  extraSubtitleInfo?: string;
  customContent?: React.ReactNode;
  className?: string;
}

// The improved local reporting card component for the matrix chart
function MatrixReportingCard({
  reporting,
  handleExport,
  title,
  matrixSelector,
  dateSelector,
  description,
  extraSubtitleInfo,
  customContent,
  className,
}: MatrixReportingCardProps) {
  return (
    <Card
      title={
        <MemoizedReportingMatrixTitle
          title={title}
          matrixSelector={matrixSelector}
          dateSelector={dateSelector}
          description={
            description ? { ...description, options: description.options || [] } : undefined
          }
          handleExport={handleExport}
        />
      }
      subtitle={<ReportingChartSubtitle extraInfo={extraSubtitleInfo} reporting={reporting} />}
      className={cn(className)}
    >
      {customContent}
    </Card>
  );
}

export default React.memo(ReparabilityImpactMatrixChart);
